import React, { useEffect, useMemo, useState } from 'react';
import {
  KendoReactGrid,
  GridColumn,
  GridCustomFilterCellProps
} from '@Shared.Angular/kendo.react/KendoReactGrid';
import { getService } from '@Client/runner.hooks/useService';
import { Guid } from '@Shared.Angular/@types/guid';
import { STATE_MAPPINGS } from '@Shared.Angular/@types/stateMappings';
import {
  CompositeFilterDescriptor,
  FilterDescriptor,
  SortDescriptor
} from '@progress/kendo-data-query';
import AutoCompleteFilter from '@Shared.Angular/flowingly.components/Filters/AutoCompleteFilter/AutoCompleteFilter';
import ActionButtons from '../ActionButtons';
import { FlowinglyPermissions } from '@Shared.Angular/flowingly.services/flowingly.constants';
import { LibraryTagsModal } from '../runner.library.tags/LibraryTagsModal';
import { ICopyProcedureResult } from './runner.library.procedures.copy.dialog.controller';
import { HeaderTdElement } from '@progress/kendo-react-data-tools';
import { GridCustomCellProps } from '@progress/kendo-react-grid';
import { StatusBadge } from '../StatusBadge';
import IProcedureOverview from '@Shared.Angular/@types/core/contracts/queryModel/procedure/procedureOverview';
import IProcedureTagSummary from '@Shared.Angular/@types/core/contracts/queryModel/procedure/procedureTagSummary';
import { DisplayStatus } from '@Shared.Angular/@types/core/contracts/constants/displayStatus';
import { TagFilter } from '@Shared.Angular/flowingly.components/TagFilter/TagFilter';
import { PublicType } from '@Shared.Angular/@types/core/contracts/constants/publicType';
import EmptyState from '@Client/runner.directives/common/EmptyState';
import KendoReactDropDownTree from '@Shared.Angular/kendo.react/KendoReactDropDownTree';
import { IProcedureSharingResult } from './runner.library.procedures.sharing.dialog.controller';
import { DateFilter } from '@Shared.Angular/flowingly.components/Filters/DateFilter/DateFilter';
import ProcedureDeleteModal from './ProcedureDeleteModal';
import { ProcedureViewMode } from '@Shared.Angular/@types/core/contracts/constants/procedureViewMode';
import GridAutoCompleteFilter from '@Shared.Angular/flowingly.components/Filters/AutoCompleteFilter/GridAutoCompleteFilter';

const LibraryProceduresGrid = () => {
  const APP_CONFIG = getService('APP_CONFIG');
  const procedureApiService = getService('procedureApiService');
  const permissions = getService('permissionsService');
  const $state = getService('$state');
  const notificationService = getService('notificationService');
  const dialogService = getService('dialogService');
  const moment = getService('momentService');
  const pubsubService = getService('pubsubService');
  const categoryApiService = getService('categoryApiService');

  const pageSize = 15;
  const [allGridItems, setAllGridItems] = useState<ILibraryProcedureOverview[]>(
    []
  );
  const [gridItemsLoaded, setGridItemsLoaded] = useState(false);
  const [viewTagsTarget, setViewTagsTarget] =
    useState<ILibraryProcedureOverview>();
  const [usedTagIds, setUsedTagIds] = useState<Guid[]>([]);
  const [tagFilterIds, setTagFilterIds] = useState<Guid[]>([]);
  const [categoryFilterId, setCategoryFilterId] = React.useState<Guid | null>(
    null
  );
  const [deleteModalTarget, setDeleteModalTarget] =
    useState<ILibraryProcedureOverview | null>(null);
  const canCreateProcedures = permissions.currentUserHasPermission(
    FlowinglyPermissions.PROCEDURE_CREATE
  );

  const canEditProcedure = (procedure: ILibraryProcedureOverview): boolean =>
    procedure.userCanEdit;

  const canDeleteProcedure = (procedure: ILibraryProcedureOverview): boolean =>
    procedure.userCanDelete;

  const canShareProcedure = (procedure: ILibraryProcedureOverview): boolean =>
    procedure.userCanEdit || procedure.userCanPublish;

  const subscriberId = 'libraryProceduresGrid';
  pubsubService.subscribeManyLate(
    [
      'PROCEDURE_CREATED',
      'PROCEDURE_UPDATED',
      'PROCEDURE_PUBLISHED',
      'PROCEDURE_DELETED'
    ],
    () => {
      getData();
    },
    subscriberId
  );

  const getData = () => {
    const promises: angular.IPromise<unknown>[] = [
      procedureApiService.deferedProcedures.promise,
      procedureApiService.deferedProcedureTags.promise
    ];
    Promise.all(promises).then(
      ([procedures, procedureTags]: [
        IProcedureOverview[],
        IProcedureTagSummary[]
      ]) => {
        const usedTagIdSet = new Set<Guid>();
        const procedureTagsMap = procedureTags.reduce((acc, tagSummary) => {
          acc[tagSummary.procedureId] = tagSummary.tagIds;
          return acc;
        }, {} as Record<string, Guid[]>);

        const allDrafts = procedures
          .filter((p) => p.published)
          .map(
            (p) =>
              ({
                ...p,
                ownerName: p.ownerName || 'Not defined',
                status:
                  p.publicType === PublicType.Public
                    ? DisplayStatus.Public
                    : p.publicType === PublicType.Intranet
                    ? DisplayStatus.PublicSSO
                    : DisplayStatus.Internal,
                lastUpdatedLocal: new Date(p.updatedDate + 'Z'),
                reviewDateLocal: p.processReviewDateUtc
                  ? new Date(`${p.processReviewDateUtc}Z`)
                  : undefined,
                tagIds: procedureTagsMap[p.id] || []
              } as ILibraryProcedureOverview)
          );

        allDrafts.forEach((d) => {
          d.tagIds.forEach((tagId) => usedTagIdSet.add(tagId));
        });
        setUsedTagIds(Array.from(usedTagIdSet));

        setAllGridItems(allDrafts);
        setGridItemsLoaded(true);
      }
    );
  };
  useEffect(() => {
    getData();
  }, []);

  const getTagFilter = () => {
    return {
      field: 'tagIds',
      operator: (itemTagIds: Guid[]) => {
        return (
          !tagFilterIds ||
          tagFilterIds.length === 0 ||
          (itemTagIds &&
            tagFilterIds.some((tagId) => itemTagIds.includes(tagId)))
        );
      }
    };
  };

  const getCategoryFilter = () => {
    return {
      field: 'categoryId',
      operator: (itemCategoryId: Guid) => {
        return !categoryFilterId || categoryFilterId === itemCategoryId;
      }
    };
  };

  const [filter, setFilter] = React.useState<CompositeFilterDescriptor>({
    logic: 'and',
    filters: [getTagFilter()]
  });

  useEffect(() => {
    const newCategoryFilter = filter.filters.find(
      (f: FilterDescriptor) => f.field === 'categoryName'
    ) as FilterDescriptor;
    const validFilters = newCategoryFilter
      ? filter.filters.filter(
          (f: FilterDescriptor) =>
            f.field !== 'categoryId' &&
            (categoryFilterId || f.field !== 'categoryName')
        )
      : filter.filters;
    setFilter({
      ...filter,
      filters: validFilters.map((f: FilterDescriptor) => {
        if (f.field === 'tagIds') {
          return getTagFilter();
        } else if (f.field === 'categoryName') {
          return getCategoryFilter();
        } else {
          return f;
        }
      })
    });
  }, [tagFilterIds, categoryFilterId]);

  const namesMemo = useMemo(
    () =>
      Array.from(new Set<string>(allGridItems.map((x) => x.name))).sort(
        (a, b) => a.localeCompare(b)
      ),
    [allGridItems]
  );
  const nameFilterCellMemo = useMemo(
    () => (props: GridCustomFilterCellProps) =>
      GridAutoCompleteFilter(props, namesMemo, 'Search by Name'),
    [namesMemo]
  );

  const creatorNamesMemo = useMemo(
    () =>
      Array.from(new Set<string>(allGridItems.map((x) => x.creatorName))).sort(
        (a, b) => a.localeCompare(b)
      ),
    [allGridItems]
  );
  const creatorFilterCellMemo = useMemo(
    () => (props: GridCustomFilterCellProps) =>
      GridAutoCompleteFilter(props, creatorNamesMemo, 'Search by Creator'),
    [creatorNamesMemo]
  );

  const ownerNamesMemo = useMemo(
    () =>
      Array.from(new Set<string>(allGridItems.map((x) => x.ownerName))).sort(
        (a, b) => a.localeCompare(b)
      ),
    [allGridItems]
  );
  const ownerFilterCellMemo = useMemo(
    () => (props: GridCustomFilterCellProps) =>
      GridAutoCompleteFilter(props, ownerNamesMemo, 'Search by Owner'),
    [ownerNamesMemo]
  );

  const categoryFilterCell = (props: GridCustomFilterCellProps) => {
    return (
      <HeaderTdElement
        columnId={props.thProps?.columnId || ''}
        {...props.thProps}
      >
        <KendoReactDropDownTree
          categories={categoryApiService.cachedCategories}
          categoryClicked={(id: Guid) => {
            props.onChange({
              field: 'categoryId',
              operator: 'eq',
              value: id
            } as any);
            setCategoryFilterId(id);
          }}
          selectedId={categoryFilterId}
          dataItemKey="id"
          textField="name"
          subItemsField="subCategories"
          defaultItemId={null}
          showFilter={true}
        />
      </HeaderTdElement>
    );
  };

  const dateFilterCell: React.FC<GridCustomFilterCellProps> = (props) => (
    <HeaderTdElement
      columnId={props.thProps?.columnId || ''}
      {...props.thProps}
    >
      <DateFilter
        value={props.value as Date | null}
        onChange={(args) =>
          props.onChange?.({
            value: args.value,
            operator: args.operator,
            syntheticEvent: args.syntheticEvent
          })
        }
      />
    </HeaderTdElement>
  );

  const statusMemo = useMemo(
    () =>
      Array.from(new Set<string>(allGridItems.map((x) => x.status))).sort(
        (a, b) => a.localeCompare(b)
      ),
    [allGridItems]
  );
  const statusFilterCell = (props) => {
    return (
      <HeaderTdElement
        columnId={props.thProps?.columnId || ''}
        {...props.thProps}
      >
        <AutoCompleteFilter
          {...props}
          data={statusMemo}
          placeholder="Search by Status"
          onChange={(e) => {
            if (e.value) {
              props.onChange({
                field: 'status',
                operator: 'eq',
                value: e.value
              } as any);
            } else {
              props.onChange({
                value: null,
                operator: ''
              });
            }
          }}
        />
      </HeaderTdElement>
    );
  };

  const initialSort = useMemo(
    () => [{ field: 'lastUpdatedLocal', dir: 'desc' } as SortDescriptor],
    []
  );

  const ellipsisCell = (props: GridCustomCellProps) => {
    const value = props.dataItem[props.field];
    return (
      <td {...props.tdProps} title={value}>
        {value}
      </td>
    );
  };

  const procedureNameCell = (props: GridCustomCellProps) => {
    const value = props.dataItem[props.field];
    return (
      <td {...props.tdProps} title={value}>
        <span data-testid="procedure-name">{value}</span>
      </td>
    );
  };

  const dateCell = (props: GridCustomCellProps) => {
    const value = props.dataItem[props.field];
    if (!value) {
      return <td {...props.tdProps}></td>;
    }
    return (
      <td
        {...props.tdProps}
        title={moment(value).format('YYYY-MM-DD HH:mm:ss')}
      >
        {moment(value).format('DD/MM/YYYY')}
      </td>
    );
  };

  const statusCell = (props: GridCustomCellProps) => {
    return (
      <td {...props.tdProps}>
        <StatusBadge status={props.dataItem.status} />
      </td>
    );
  };

  const openLockDialog = (procedure: ILibraryProcedureOverview) => {
    dialogService
      .showDialog({
        template:
          'Client/runner.library/runner.library.procedures/runner.library.procedures.sharing.dialog.tmpl.html',
        controller: 'procedureSharingDialogController',
        appendClassName: 'ngdialog-normal',
        data: {
          procedureId: procedure.id,
          procedureName: procedure.name,
          procedurePublicType: procedure.publicType
        }
      })
      .then((result: IProcedureSharingResult) => {
        if (dialogService.isCloseModalWithCancelAction(result)) {
          return;
        }

        procedureApiService
          .updateProcedurePublicType(result.id, result.publicType)
          .then(() => {
            procedure.publicType = result.publicType;
            procedure.status =
              result.publicType === null
                ? DisplayStatus.Internal
                : result.publicType === PublicType.Public
                ? DisplayStatus.Public
                : DisplayStatus.PublicSSO;
            const items = allGridItems.map((item) =>
              item.id === procedure.id ? { ...procedure } : item
            );
            setAllGridItems(items);
            notificationService.showSuccessToast(
              `${procedure.name} Sharing Updated`
            );
          });
      });
  };

  const goToProcedureEditor = (dataItem: ILibraryProcedureOverview) => {
    $state.go(
      STATE_MAPPINGS.procedure,
      { procedureId: dataItem.id, viewMode: ProcedureViewMode.Edit },
      {}
    );
  };

  const copyProcedure = (dataItem: ILibraryProcedureOverview) => {
    dialogService
      .showDialog({
        template:
          'Client/runner.library/runner.library.procedures/runner.library.procedures.copy.dialog.tmpl.html',
        controller: 'procedureCopyDialogController',
        appendClassName: 'ngdialog-normal',
        data: {
          existingProcedureId: dataItem.id,
          existingProcedureName: dataItem.name
        }
      })
      .then((result: ICopyProcedureResult) => {
        if (dialogService.isCloseModalWithCancelAction(result)) {
          return;
        }

        $state.go(STATE_MAPPINGS.procedure, {
          procedureId: result.id,
          viewMode: ProcedureViewMode.Edit
        });
      });
  };

  const deleteProcedure = () => {
    if (!deleteModalTarget) {
      return;
    }

    procedureApiService
      .deleteProcedure(deleteModalTarget.id)
      .then(() => {
        notificationService.showSuccessToast('SOP successfully Deleted.');
        setAllGridItems((items) =>
          items.filter((item) => item.id !== deleteModalTarget.id)
        );
        setDeleteModalTarget(null);
      })
      .catch((error: { status?: number }) =>
        notificationService.showErrorToast(
          error?.status === 403
            ? 'You do not have permission to delete this SOP'
            : 'Failed to delete SOP'
        )
      );
  };

  const generateActionsCell = (props: GridCustomCellProps) => {
    const procedure: ILibraryProcedureOverview = props.dataItem;
    let lockHandler: () => void;
    let editHandler: () => void;
    let copyHandler: () => void;
    const tagHandler = APP_CONFIG.enableTagging
      ? () => setViewTagsTarget(procedure)
      : undefined;
    let deleteHandler: () => void;

    if (canShareProcedure(procedure)) {
      lockHandler = () => openLockDialog(procedure);
    }
    if (canEditProcedure(procedure)) {
      editHandler = () => goToProcedureEditor(procedure);
    }
    if (canCreateProcedures) {
      copyHandler = () => copyProcedure(procedure);
    }
    if (canDeleteProcedure(procedure)) {
      deleteHandler = () => setDeleteModalTarget(procedure);
    }
    return (
      <td {...props.tdProps}>
        <ActionButtons
          showButtons={{
            procedureLock: true,
            edit: true,
            copy: true,
            tags: APP_CONFIG.enableTagging,
            delete: true
          }}
          onProcedureLockClick={lockHandler}
          onEditClick={editHandler}
          onCopyClick={copyHandler}
          onTagsClick={tagHandler}
          onDeleteClick={deleteHandler}
        />
      </td>
    );
  };

  const emptyStateBody = 'Get started and create your first SOP.';

  return (
    (!gridItemsLoaded && <div></div>) ||
    (gridItemsLoaded && allGridItems.length > 0 && (
      <div className="library-grid">
        <TagFilter usedTagIds={usedTagIds} onApply={setTagFilterIds} />
        <KendoReactGrid
          id="library-procedures-grid"
          autoProcessData={{
            filter: true,
            search: true,
            sort: true,
            group: false,
            page: true
          }}
          data={allGridItems}
          dataItemKey="id"
          defaultSort={initialSort}
          defaultTake={pageSize}
          filter={filter}
          filterable={true}
          onFilterChange={(filterChangeEvent) => {
            setFilter(filterChangeEvent.filter);
          }}
          pageable={{
            buttonCount: 10,
            info: true,
            previousNext: true
          }}
          sortable={true}
          resizable={true}
        >
          <GridColumn
            field="name"
            title="Name"
            className="column-name ellipsis-column"
            cells={{ data: procedureNameCell, filterCell: nameFilterCellMemo }}
          />
          <GridColumn
            field="creatorName"
            title="Created By"
            className="column-created-by ellipsis-column"
            cells={{ data: ellipsisCell, filterCell: creatorFilterCellMemo }}
          />
          <GridColumn
            field="ownerName"
            title="Owner"
            className="column-owner ellipsis-column"
            cells={{ data: ellipsisCell, filterCell: ownerFilterCellMemo }}
          />
          <GridColumn
            field="lastUpdatedLocal"
            title="Last Updated"
            filter="date"
            className="column-last-updated"
            cells={{ data: dateCell, filterCell: dateFilterCell }}
          />
          <GridColumn
            field="reviewDateLocal"
            title="Review Date"
            filter="date"
            className="column-review-date"
            cells={{ data: dateCell, filterCell: dateFilterCell }}
          />
          <GridColumn
            field="categoryName"
            title="Category"
            className="column-category ellipsis-column"
            cells={{ data: ellipsisCell, filterCell: categoryFilterCell }}
          />
          <GridColumn
            field="viewCount"
            title="Views"
            className="column-views ellipsis-column"
            filterable={false}
          />
          <GridColumn
            field="publishedTo"
            title="Published To"
            className="column-published-to ellipsis-column"
            filterable={false}
            cells={{ data: ellipsisCell }}
          />
          <GridColumn
            field="status"
            title="Status"
            className="column-status"
            cells={{
              data: statusCell,
              filterCell: statusFilterCell
            }}
          />
          <GridColumn
            title="Actions"
            sortable={false}
            filterable={false}
            resizable={false}
            width={250}
            className="column-actions"
            cells={{
              data: generateActionsCell
            }}
          />
        </KendoReactGrid>
        {viewTagsTarget && (
          <LibraryTagsModal
            title={viewTagsTarget.name}
            onClose={() => setViewTagsTarget(undefined)}
            procedureId={viewTagsTarget.id}
          />
        )}
        {deleteModalTarget && (
          <ProcedureDeleteModal
            procedureName={deleteModalTarget.name}
            procedureId={deleteModalTarget.id}
            closeModal={() => setDeleteModalTarget(null)}
            deleteAction={deleteProcedure}
          />
        )}
      </div>
    )) ||
    (gridItemsLoaded && allGridItems.length === 0 && (
      <EmptyState
        summaryText="Build your process Library"
        bodyText={emptyStateBody}
      />
    ))
  );
};

export default LibraryProceduresGrid;

export interface ILibraryProcedureOverview extends IProcedureOverview {
  status: DisplayStatus;
  lastUpdatedLocal: Date;
  reviewDateLocal?: Date;
  tagIds: Guid[];
}
